import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from 'react-router-dom';

// 📍 แก้จุดที่ 1: เพิ่มการ import loadDepositFirst
import { apiServices } from "../../../../../../../@core/services/apiReqService";
import { cancenDeposit, checkDeposit, loadDepositFirst } from "../../../../../../../@core/services/depositService";

// Redux
import { setStatusSpinner } from "../../../../../../../@core/redux/actions/app/spinner";
import { setDataAlert } from "../../../../../../../@core/redux/actions/app/alert";
import { setDepositData } from "../../../../../../../@core/redux/actions/deposit/index"; // 📍 ลืม import ตัวนี้สำหรับ setDepositData ด้วยครับ

// @Utility
import { getStorage, removeStorage, setStorage } from "../../../../../../../@utility/app/storage";
import alertText from "../../../../../../../@utility/app/alertText";

// Components
import FormDeposit from "./FormDeposit";
import BoxProcessP2P from "./BoxProcess";
import BoxSlipP2P from "./BoxSlip";
import BoxCancel from "./BoxCancel";
import BoxCompleted from "./BoxCompleted";

// Icons (MUI v4)
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

// CSS
import styles from "./../../../../assets/css/Style.module.css";
import modalStyles from "./../../../../assets/css/Modal.module.css";

// ─── HELPER FUNCTIONS ────────────────────────────────────────────────────────
const safeGetPaymentResult = () => {
  try {
    let localSaved = getStorage("paymentResult");
    if (!localSaved) return null;

    if (typeof localSaved === "string") {
      localSaved = localSaved.trim() !== "" ? JSON.parse(localSaved) : null;
    }

    if (!localSaved || Object.keys(localSaved).length === 0) return null;
    return localSaved;
  } catch (error) {
    console.error("Parse paymentResult error:", error);
    return null;
  }
};

const buildPaymentResultKey = (data) => {
  if (!data || data === "error") return "";
  return [
    data?.id || "",
    data?.status || "",
    data?.matched_amount || "",
    data?.remaining_amount || "",
    data?.external_ref || "",
    data?.message || "",
    data?.legs?.[0]?.appointment_deadline || "",
    data?.legs?.[0]?.pay_to?.account_number || ""
  ].join("|");
};

const CANCEL_STATUSES = new Set([
  "cancelled",
  "system-cancelled",
  "expired",
  "failed",
  "no_match",
  "match_failed",
  "unmatched"
]);

const isCancelP2PStatus = (status) => CANCEL_STATUSES.has(status);

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────
const FormP2P = memo(({
  typePage,
  depositItems,
  channelInfo,
  bankList,
  setMenuSelect
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [money, setMoney] = useState("");
  const [p2pMode, setP2pMode] = useState("form");
  const [timeDepositProcess, setTimeDepositProcess] = useState(null);
  const [paymentResultState, setPaymentResultState] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  const paymentResultRef = useRef(null);
  const isPollingFetchingRef = useRef(false);

  // ─── ACTIONS ─────────────────────────────────────────────────────────────
  const openBox = useCallback((snackbarJson) => {
    dispatch(
      setDataAlert({
        type: snackbarJson.statusAlert,
        title: snackbarJson.titleAlert,
        text: snackbarJson.textAlert,
        action: true,
        redirect: null,
        show: true,
        buttonText: "ตกลง"
      })
    );
  }, [dispatch]);

  const onCancel = useCallback(() => {
    setP2pMode("form");
    if (setMenuSelect) setMenuSelect("home");
    else history.push("/deposit/home");
  }, [setMenuSelect, history]);

  const reloadData = (page) => {
    loadDepositFirst().then((data) => {
      const statusCode = data.code;

      if (statusCode === 200 || statusCode === 0) {
        if (data?.result?.pay_src) {
          setStorage("paySrc", data?.result?.pay_src);
        }

        if (data?.result?.pay_mode) {
          setStorage("payMode", data?.result?.pay_mode);
        }

        if (data?.result?.pay_account?.account) {
          setStorage("payAccount", JSON.stringify(data?.result?.pay_account));
        }

        if (data?.result?.pay_amount) {
          setStorage("payAmount", data?.result?.pay_amount);
        }

        if (data?.result?.pay_time_out) {
          setStorage("payTimeOut", data?.result?.pay_time_out);
        }

        const payAccountData = data.result?.pay_account;

        if (payAccountData?.code) {
          setStorage("payAccount", JSON.stringify(data.result?.pay_account));
        } else {
          const payResultData = data.result?.payment_result?.account;

          if (payResultData) {
            setStorage(
              "payAccount",
              JSON.stringify({
                account: payResultData?.account_no,
                code: payResultData?.bank_code,
                name: payResultData?.account_name,
              })
            );
          } else {
            setStorage("payAccount", JSON.stringify(data.result?.pay_account));
          }
        }

        if (data.result?.pay_mode === "crypto") {
          setStorage("paySrc", data.result?.pay_account?.account);
        }

        if (data.result?.payment_result?.crypto_fx_rate) {
          setStorage("payRate", data.result.payment_result.crypto_fx_rate);
        }

        if (data.result?.pay_amount) {
          const finalAmount = data.result?.payment_result?.crypto_amount || data.result?.pay_amount;
          setStorage("payAmount", finalAmount);
        }

        if (data.result?.qrcode_time_out) {
          const firstEventTimestamp = Date.now(data.result?.qrcode_time_out);
          setStorage("depositPromptPayTime", firstEventTimestamp);
        } else {
          const firstEventTimestamp = Date.now();
          const timeStart = firstEventTimestamp + 5 * 60 * 1000;
          setStorage("depositPromptPayTime", timeStart);
        }

        if (data?.result?.payment_result) {
          setStorage("paymentResult", JSON.stringify(data.result?.payment_result));
        }

        dispatch(setDepositData(data.result));
        setStorage("processDeposit", data.result.id);

        if (page === "home") {
          history.push("/");
        } else {
          history.push("/deposit/pending/manual");
        }

        return;
      }

      if (statusCode === 2 || statusCode === 4) {
        let snackbarJson = alertText(statusCode);
        openBox(snackbarJson);

        setTimeout(() => {
          window.location.href = "/";
        }, 5000);

        return;
      }

      let snackbarJson = alertText(statusCode);
      openBox(snackbarJson);
    });
  };

  const cancelTransaction = useCallback(() => {
    dispatch(setStatusSpinner({ status: true, textProcess: null }));

    cancenDeposit().then((data) => {
      const statusCode = data?.code;
      dispatch(setStatusSpinner({ status: false, textProcess: null }));

      if (statusCode === 200 || statusCode === 0) {
        removeStorage("paymentResult");
        removeStorage("paySrc");
        removeStorage("payMode");
        removeStorage("payAccount");
        removeStorage("payAmount");
        removeStorage("payTimeOut");
        removeStorage("processDeposit");
        removeStorage("depositPromptPayTime");
        removeStorage("channelPaySelect");
        removeStorage("payRate");
        onCancel();
      } else {
        openBox({
          statusShow: true,
          statusAlert: "warning",
          titleAlert: "แจ้งเตือน",
          textAlert: "ยกเลิกรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
        });
      }
    });
  }, [dispatch, onCancel, openBox]);

  // ─── 1. DATA INITIALIZATION ──────────────────────────────────────────────
  useEffect(() => {
    if (!depositItems) return;

    if (depositItems.mode === "deposit") {
      removeStorage("paymentResult");
      paymentResultRef.current = null;
      setPaymentResultState(null);
      setTimeDepositProcess(null);
      setP2pMode("form");
      return;
    }

    if (depositItems.mode === "pay") {
      const readDataResult = depositItems?.payment_result || safeGetPaymentResult();

      if (!readDataResult) {
        paymentResultRef.current = "error";
        setPaymentResultState("error");
        return;
      }

      const prevKey = buildPaymentResultKey(paymentResultRef.current);
      const nextKey = buildPaymentResultKey(readDataResult);

      if (prevKey !== nextKey) {
        paymentResultRef.current = readDataResult;
        setPaymentResultState(readDataResult);
      }
    }
  }, [depositItems?.mode, depositItems?.payment_result]);

  // ─── 2. POLLING ENGINE ───────────────────────────────────────────────────
  useEffect(() => {
    if (depositItems?.mode !== "pay" || !depositItems?.id) return;

    let isMounted = true;
    let intervalId = null;

    const fetchP2PStatus = async () => {
      if (isPollingFetchingRef.current) return;
      isPollingFetchingRef.current = true;

      try {
        let snackbarJson = {};

        checkDeposit(depositItems.id).then((data) => {
          const statusCode = data.code;

          if (statusCode === 200 || statusCode === 0) {
            switch (data.result.status) {
              case 0:
              case 1:
                break;

              case 2:
                snackbarJson = {
                  statusShow: true,
                  statusAlert: "success",
                  titleAlert: "แจ้งเตือน",
                  textAlert: "ทำรายการเติมเงินสำเร็จ !",
                };

                openBox(snackbarJson);

                // 📍 แก้จุดที่ 2: เปลี่ยน checkTime เป็น intervalId
                if (intervalId) {
                  clearInterval(intervalId);
                }

                removeStorage("processDeposit");
                reloadData("home");
                break;

              case 3:
                // 📍 แก้จุดที่ 2: เปลี่ยน checkTime เป็น intervalId
                if (intervalId) {
                  clearInterval(intervalId);
                }

                snackbarJson = {
                  statusShow: true,
                  statusAlert: "warning",
                  titleAlert: "แจ้งเตือน",
                  textAlert: "ยกเลิกรายการเติมเงิน เนื่องจากมีเงื่อนไขบางอย่างไม่ถูกต้อง! กรุณาทำรายการใหม่อีกครั้ง",
                };

                openBox(snackbarJson);
                removeStorage("processDeposit");

                if (channelInfo?.event_select === "auto") {
                  reloadData("home");
                } else {
                  reloadData("/");
                }

                break;

              default:
                // 📍 แก้จุดที่ 2: เปลี่ยน checkTime เป็น intervalId
                if (intervalId) {
                  clearInterval(intervalId);
                }

                history.push("/deposit/menu");
                removeStorage("processDeposit");
                break;
            }

            return;
          }

          if (statusCode === 2 || statusCode === 4) {
            snackbarJson = alertText(statusCode);
            openBox(snackbarJson);

            setTimeout(() => {
              window.location.href = "/";
            }, 5000);

            return;
          }

          snackbarJson = alertText(statusCode);
          openBox(snackbarJson);
        });

        const response = await apiServices(
          { id: depositItems.id },
          "post",
          "/customer/payment-v3/deposit/status-p2p"
        );

        if (!isMounted) return;

        if (response?.code === 0 && response?.result) {
          const p2pData = response.result;
          const prevKey = buildPaymentResultKey(paymentResultRef.current);
          const nextKey = buildPaymentResultKey(p2pData);

          if (prevKey !== nextKey) {
            setStorage("paymentResult", JSON.stringify(p2pData));
            paymentResultRef.current = p2pData;
            setPaymentResultState(p2pData);
          }
        }
      } catch (error) {
        console.error("Polling P2P Status Error:", error);
      } finally {
        isPollingFetchingRef.current = false;
      }
    };

    intervalId = setInterval(fetchP2PStatus, 60000);
    fetchP2PStatus();

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [depositItems?.mode, depositItems?.id]);

  // ─── 3. STATE MACHINE ────────────────────────────────────────────────────
  useEffect(() => {
    if (!paymentResultState || paymentResultState === "error") return;

    const apiStatus = paymentResultState.status;
    const firstLeg = paymentResultState.legs?.[0] || {};
    const hasPayTo = !!firstLeg.pay_to?.account_number;
    const hasSlip = !!depositItems?.slip;

    const pendingAmount = depositItems?.pay_amount ?? paymentResultState?.matched_amount ?? depositItems?.amount;

    if (pendingAmount != null && pendingAmount !== "") {
      const newMoney = String(pendingAmount);
      setMoney((prev) => (prev === newMoney ? prev : newMoney));
    }

    const nextTime = firstLeg?.appointment_deadline || null;
    if (nextTime) {
      setTimeDepositProcess((prev) => (prev === nextTime ? prev : nextTime));
    } else {
      setTimeDepositProcess(null);
    }

    let isLocallyExpired = false;
    if (!hasSlip && firstLeg.appointment_deadline) {
      const deadlineTimestamp = Date.parse(firstLeg.appointment_deadline);
      if (!Number.isNaN(deadlineTimestamp) && Date.now() >= deadlineTimestamp) {
        isLocallyExpired = true;
      }
    }

    const changeMode = (nextMode) => setP2pMode((prev) => (prev === nextMode ? prev : nextMode));

    if (apiStatus === "completed") {
      changeMode("completed");
      return;
    }

    if (hasSlip || apiStatus === "processing") {
      changeMode("slip-review");
      return;
    }

    if (isLocallyExpired) {
      setIsExpired(true);
    }

    if (isCancelP2PStatus(apiStatus) || isLocallyExpired) {
      changeMode("cancel");
      return;
    }

    if (hasPayTo) {
      changeMode("matched");
      return;
    }

    if (apiStatus === "pending" || !hasPayTo) {
      changeMode("matching");
      return;
    }
  }, [
    paymentResultState,
    depositItems?.pay_amount,
    depositItems?.amount,
    depositItems?.slip,
    depositItems?.expires_at
  ]);

  // ─── 4. RENDERER ─────────────────────────────────────────────────────────
  const renderContentProcess = () => {
    if (paymentResultState === "error") {
      return (
        // 📍 แก้จุดที่ 3: เอา style={{ paddingBottom: "50px" }} ออกไปใช้ Class boxPaddingBottom50
        <div className={[styles.boxMarginFormTop, "boxPaddingBottom50"].join(" ")}>
          <div className={styles.boxDetailTop}>
            <div className="boxDetailQRCodeCancel center bgCard">
              <ErrorOutlineIcon className="boxDetailQRCodeCancelIcon" />
              <div className={styles.boxMarginTop}>
                <div className="boxDetailQRCodeCancelTextTitle">แจ้งเตือน</div>
                <div className={styles.boxMarginTop}>
                  ขณะนี้มีผู้ใช้งานจำนวนมาก กรุณารอสักครู่ แล้วลองทำรายการใหม่อีกครั้ง
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (p2pMode === "form") {
      return (
        <FormDeposit
          channelInfo={channelInfo}
          bankList={bankList}
          openBox={openBox}
          typePage={typePage}
          setPaymentResultState={setPaymentResultState}
        />
      );
    }

    if (!paymentResultState) return null;

    switch (p2pMode) {
      case "matching":
      case "matched":
        return (
          <BoxProcessP2P
            money={money}
            depositItems={depositItems}
            paymentResultState={paymentResultState}
            bankList={bankList}
            setMode={setP2pMode}
            openBox={openBox}
            timeDepositProcess={timeDepositProcess}
            cancelTransaction={cancelTransaction}
            setTimeDepositProcess={setTimeDepositProcess}
            isExpired={isExpired}
            setIsExpired={setIsExpired}
          />
        );

      case "slip":
      case "slip-review":
        return (
          <BoxSlipP2P
            p2pMode={p2pMode}
            money={money}
            depositItems={depositItems}
            paymentResultState={paymentResultState}
            bankList={bankList}
            setMode={setP2pMode}
            openBox={openBox}
            timeDepositProcess={timeDepositProcess}
            setTimeDepositProcess={setTimeDepositProcess}
          />
        );

      case "completed":
        return (
          <BoxCompleted
            displayAmount={
              paymentResultState?.amount ||
              paymentResultState?.matched_amount ||
              depositItems?.amount ||
              money ||
              0
            }
          />
        );

      case "cancel":
        return (
          <BoxCancel
            status={paymentResultState?.status || depositItems?.payment_result?.status}
            message={paymentResultState?.message}
            cancelTransaction={cancelTransaction}
            isExpired={isExpired}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className={modalStyles.boxTitle}>
        {channelInfo?.name || "ฝากเงิน P2P"}
      </div>

      <div id="DepositInput">
        {renderContentProcess()}
      </div>
    </>
  );
});

export default FormP2P;